'use client';
import { useScreenSize } from '@/hooks/useScreenSizeDecodo';
import { cn } from '@/lib/utils';

import { slugify } from '@/components/atoms/layout/utils';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import Heading from '@/decodo/components/atoms/Heading';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/decodo/components/atoms/Toggle/useCMSToggle';
import {
  FeatureListStrapi,
  FeatureListStrapiProps,
} from '@/features/shared/FeatureListStrapiDC';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContentDC';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';

export interface ImageTextProps {
  image: ImageStrapiProps[];
  header?: BlockHeader;
  toggle?: ToggleCMSProps;
  title: string;
  description?: RichTextFieldStrapiProps;
  buttons?: LinkButtonStrapiProps[];
  layout: 'one-column' | 'two-columns' | 'two-columns-reverse';
  featureList?: FeatureListStrapiProps;
}

export function ImageTextStrapi({
  block,
  ...rest
}: BlockComponentModel<ImageTextProps>) {
  const {
    image: images,
    header,
    toggle,
    featureList,
    title,
    description,
    buttons,
    layout = 'two-columns',
  } = block;
  const { isDesktop } = useScreenSize();

  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle<
    typeof images
  >(images, toggle);

  const withConditionalExpansion = (
    imageBlock: ImageStrapiProps
  ): ImageStrapiProps => {
    return {
      ...imageBlock,
      isExpandable: isDesktop && imageBlock.isExpandable,
    };
  };

  // TODO remove support for single image after migration
  const selectedImages = (
    Array.isArray(images) ? filteredToggleList || images : [images]
  ).map(withConditionalExpansion);

  const layoutClasses = {
    'one-column': 'md:flex-col',
    'two-columns': cn('md:flex-row items-start flex-col'),
    'two-columns-reverse': cn('md:flex-row-reverse items-start flex-col'),
  } as const;

  const slugifiedName = slugify(title ? title : '');

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.TEXT_IMAGE}>
      <div>
        {layout !== 'one-column' ? (
          <BlockHeaderContent
            {...header}
            classNames={{
              children: cn(
                layoutClasses[layout],
                'flex-nowrap md:justify-between justify-between gap-6 md:gap-20'
              ),
            }}
          >
            <div>
              <ImageStrapi
                inner={{
                  classNames: {
                    image: 'rounded md:max-w-[360px] md:max-h-[360px]',
                    // TODO: return to this as part of AWD-4527
                    // image:
                    //   'w-full h-full rounded md:max-w-[624px] md:max-h-[624px]',
                  },
                }}
                width={360}
                height={360}
                block={selectedImages[0]}
              />
            </div>
            <div className='w-full flex-1 text-left' id={slugifiedName}>
              {title && <Heading level='h2'>{title}</Heading>}
              {description && (
                <div
                  className='mt-4 text-gray-500 md:mt-6'
                  data-tracker-component='description'
                >
                  <RichTextFieldStrapi
                    block={description}
                    classNames={{ paragraph: 'pb-4' }}
                  />
                </div>
              )}
              {featureList && (
                <div className='mt-8 flex flex-wrap  gap-6 max-md:gap-y-4 md:mt-9'>
                  <FeatureListStrapi
                    features={featureList.features}
                    icon={featureList.icon}
                    classNames={{ item: 'text-body-l-medium' }}
                  />
                </div>
              )}
              {!!buttons?.length && (
                <div
                  className='mt-8 flex gap-4 md:mt-9'
                  data-tracker-component='actions'
                >
                  {buttons.map((button, index) => (
                    <LinkButtonStrapi
                      key={index}
                      block={button}
                      inner={{ classNames: { root: 'w-full md:w-auto' } }}
                    />
                  ))}
                </div>
              )}
            </div>
          </BlockHeaderContent>
        ) : (
          <BlockHeaderContent
            {...header}
            classNames={{
              root: layoutClasses[layout],
            }}
            topChildren={ToggleNode ? ToggleNode() : undefined}
          >
            <ImageStrapi
              inner={{ classNames: { image: 'w-full rounded-xl' } }}
              block={selectedImages[0]}
            />
          </BlockHeaderContent>
        )}
      </div>
    </BlockWrapper>
  );
}
